/**
 * Case evidence rail — server-authoritative retained media under legal hold.
 * CMC Case file receives / shows; capture and upload stay on their source surfaces.
 * Soft≠Live: Soft fixtures paint phone uploads; Soft invent Live phone writes forbidden.
 * Click a row → small viewer popup (Soft placeholder or authenticated Live image).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { html, Ico, useEsc, useOverlayLock, stop } from "./ui.js";
import { api } from "./api.js";

const SOFT_PHONE_MEDIA = {
  "soft-inc-p1-gate": [
    {
      id: "soft-phone-gate-1", kind: "snapshot", author: "Chen Wei", author_role: "UNIT-7",
      device: "phone", note: "Cut at pedestrian lane · seal requested",
      at: Date.now() - 90000, soft_demo: true, event_id: "soft-inc-p1-gate",
      trigger_kind: "personnel_upload",
    },
    {
      id: "soft-phone-gate-2", kind: "recording", author: "Chen Wei", author_role: "UNIT-7",
      device: "phone", note: "Approach clip · dual unit staging",
      at: Date.now() - 45000, soft_demo: true, event_id: "soft-inc-p1-gate",
      trigger_kind: "personnel_upload",
    },
  ],
  "soft-inc-p2-plant": [
    {
      id: "soft-phone-plant-1", kind: "snapshot", author: "Maya Ortiz", author_role: "UNIT-3",
      device: "phone", note: "Heat plume from west hydrant approach",
      at: Date.now() - 60000, soft_demo: true, event_id: "soft-inc-p2-plant",
      trigger_kind: "personnel_upload",
    },
  ],
  "soft-inc-p2-dock": [
    {
      id: "soft-phone-dock-1", kind: "recording", author: "Omar Hassan", author_role: "UNIT-12",
      device: "phone", note: "Plate mismatch · vehicle still on pad",
      at: Date.now() - 40000, soft_demo: true, event_id: "soft-inc-p2-dock",
      trigger_kind: "personnel_upload",
    },
  ],
  "soft-inc-p3-lobby": [
    {
      id: "soft-phone-lobby-1", kind: "upload", author: "Aisha Rahman", author_role: "UNIT-1",
      device: "phone", note: "On scene · elevators held · wait proof",
      at: Date.now() - 30000, soft_demo: true, event_id: "soft-inc-p3-lobby",
      trigger_kind: "personnel_upload",
    },
  ],
  "soft-inc-resolved": [
    {
      id: "soft-phone-stair-1", kind: "snapshot", author: "UNIT-9", author_role: "UNIT-9",
      device: "phone", note: "Contractor badge · false alarm close",
      at: Date.now() - 3600000, soft_demo: true, event_id: "soft-inc-resolved",
      trigger_kind: "personnel_upload",
    },
  ],
};

function kindLabel(kind) {
  const k = String(kind || "").toLowerCase();
  if (k === "snapshot" || k === "screenshot") return "Snapshot";
  if (k === "recording") return "Record";
  if (k === "camera_live" || k === "video" || k === "session") return "Live";
  if (k === "upload") return "Upload";
  return "Evidence";
}

const CHAIN_LABEL = {
  event: "Event",
  team_run: "Team run",
  dispatch: "Dispatch",
  camera: "Camera",
  video: "Live",
  screenshot: "Snapshot",
  recording: "Record",
};

function fmtWhen(at) {
  const n = Number(at) || 0;
  if (!n) return "—";
  try {
    return new Date(n).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return "—";
  }
}

function isClip(row) {
  const k = String((row && (row.kind || row.media_kind)) || "").toLowerCase();
  const mime = String((row && row.mime) || "").toLowerCase();
  return k === "recording" || mime.startsWith("video/");
}

function blobSrcOf(row) {
  if (row && row.image_url) return row.image_url;
  if (!row || !row.blob) return "";
  try {
    if (typeof URL !== "undefined" && URL.createObjectURL) {
      return URL.createObjectURL(row.blob);
    }
  } catch {
    /* ignore */
  }
  return "";
}

/**
 * Receive-only phone media rail for one Case (dispatched unit phones → Case desk).
 */
export function CaseFieldMedia({ incidentId, softDemo }) {
  const softRows = useMemo(
    () => (softDemo ? (SOFT_PHONE_MEDIA[incidentId] || []).map((r) => ({ ...r })) : []),
    [softDemo, incidentId],
  );
  const [serverRows, setServerRows] = useState([]);
  const [chain, setChain] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [view, setView] = useState(null);
  const [viewSrc, setViewSrc] = useState("");

  const reloadServer = useCallback(async () => {
    if (!incidentId || softDemo) {
      setServerRows([]);
      setChain([]);
      setLoadError("");
      return;
    }
    try {
      const payload = await api(`/incidents/${encodeURIComponent(incidentId)}/evidence?limit=160`);
      let index = null;
      try {
        const packed = await api(`/incidents/${encodeURIComponent(incidentId)}/evidence/index?limit=160`);
        index = packed && packed.index;
      } catch {
        index = null;
      }
      setServerRows((payload.items || []).map((item) => ({
        ...item,
        id: item.item_id,
        kind: item.artifact_kind,
        at: Number(item.attached_at || 0) * 1000,
        author: item.attached_by,
        author_role: item.source_device_id || "Case evidence",
        note: item.hold_reason || item.retention_policy,
        image_url: item.artifact_kind === "snapshot"
          ? `/ops/cameras/frames/${encodeURIComponent(item.artifact_ref)}/image`
          : "",
      })));
      setChain(Array.isArray(index && index.chain) ? index.chain.slice(0, 12) : []);
      setLoadError("");
    } catch (e) {
      setServerRows([]);
      setChain([]);
      /* Honest empty ≠ Soft invent: surface catalog failure separately. */
      setLoadError(String((e && e.message) || e || "evidence_catalog_unavailable"));
    }
  }, [incidentId, softDemo]);

  useEffect(() => {
    reloadServer();
    if (softDemo || typeof window === "undefined") return undefined;
    const id = setInterval(reloadServer, 8000);
    return () => clearInterval(id);
  }, [reloadServer, softDemo]);

  useEffect(() => () => {
    if (viewSrc && viewSrc.startsWith("blob:")) {
      try { URL.revokeObjectURL(viewSrc); } catch { /* ignore */ }
    }
  }, [viewSrc]);

  const closeView = useCallback(() => {
    setView(null);
    setViewSrc((prev) => {
      if (prev && prev.startsWith("blob:")) {
        try { URL.revokeObjectURL(prev); } catch { /* ignore */ }
      }
      return "";
    });
  }, []);
  const onEsc = useCallback(() => { if (view) closeView(); }, [view, closeView]);
  useEsc(onEsc);
  useOverlayLock(!!view);

  const openView = (row) => {
    if (!row) return;
    if (viewSrc && viewSrc.startsWith("blob:")) {
      try { URL.revokeObjectURL(viewSrc); } catch { /* ignore */ }
    }
    const src = softDemo ? "" : blobSrcOf(row);
    setViewSrc(src);
    setView(row);
  };

  const rows = softDemo ? softRows : serverRows;

  if (!incidentId) return null;

  return html`
    <section className="cases-v2__field-media" aria-label="Case media">
      <div className="cases-v2__section-head">
        <div>
          <span>Evidence on this event</span>
          <small>Live, snapshot, and record stay distinct. Tap a row to open.</small>
        </div>
        <b>${rows.length}</b>
      </div>

      ${softDemo ? html`
        <p className="cases-v2__field-media-note">Practice media — Soft preview only.</p>
      ` : html`
        <p className="cases-v2__field-media-note">
          Server-retained evidence · legal hold. Export the folio from Close.
        </p>
        ${chain.length ? html`<ol className="cases-v2__evidence-chain" aria-label="Evidence chain">
          ${chain.map((row) => html`<li key=${row.step + row.id} data-step=${row.step}>
            <abbr>${CHAIN_LABEL[row.step] || row.step}</abbr>
          </li>`)}
        </ol>` : null}
      `}

      ${!softDemo && loadError
        ? html`<p className="cases-v2__field-media-empty" data-evidence-load="error">
            Server evidence catalog unavailable — not inventing Soft media.
          </p>`
        : rows.length === 0
        ? html`<p className="cases-v2__field-media-empty">No retained evidence on this event yet.</p>`
        : html`<ul className="cases-v2__field-media-list">
            ${rows.map((row) => html`
              <li key=${row.id} data-kind=${row.kind || "media"} data-device="server">
                <button type="button" className="cases-v2__field-media-open"
                  onClick=${() => openView(row)}
                  aria-label=${"Open " + kindLabel(row.kind)}>
                  <abbr>${kindLabel(row.kind)}</abbr>
                  <span>
                    <strong>${row.author_role || "Unit"} · ${row.author || "phone"}</strong>
                    <em>${row.note || row.detail || "—"}</em>
                    <small className="cases-v2__field-media-src">Tap to view</small>
                  </span>
                  <time>${fmtWhen(row.at)}</time>
                </button>
              </li>`)}
          </ul>`}

      ${view ? html`
        <div className="cases-v2__media-pop" role="dialog" aria-modal="true"
          aria-label="Media viewer" onMouseDown=${closeView}>
          <div className="cases-v2__media-pop-card" onMouseDown=${stop}>
            <header>
              <div>
                <span>${kindLabel(view.kind)}${softDemo ? " · Soft" : ""}</span>
                <strong>${view.author_role || "Unit"} · ${view.author || "phone"}</strong>
                <p>${view.note || view.detail || "—"}</p>
              </div>
              <button type="button" className="btn btn--ghost btn--sm" onClick=${closeView}
                aria-label="Close viewer">
                <${Ico} d="close" w=${14}/> Close
              </button>
            </header>
            <div className="cases-v2__media-pop-stage" data-kind=${isClip(view) ? "clip" : "still"}>
              ${viewSrc
                ? (isClip(view)
                  ? html`<video src=${viewSrc} controls playsInline/>`
                  : html`<img src=${viewSrc} alt=${view.note || "Case media"}/>`)
                : html`<div className="cases-v2__media-pop-soft" data-clip=${isClip(view) ? "1" : "0"}>
                    <i aria-hidden="true"/>
                    <b>${kindLabel(view.kind)}</b>
                    <em>${softDemo
                      ? "Practice frame — Soft invent Live media forbidden."
                      : (view.artifact_ref
                        ? ("ID " + view.artifact_ref + " · file stays local, not on this server.")
                        : "No file attached on this row yet.")}</em>
                  </div>`}
            </div>
            <footer>
              <time>${fmtWhen(view.at)}</time>
              <span>${incidentId}</span>
            </footer>
          </div>
        </div>
      ` : null}
    </section>
  `;
}

export default CaseFieldMedia;
